import { useDispatch } from 'react-redux';
import {
  generatePath,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import AnalyticsLocation from '@/constants/AnalyticsLocation';
import MultiActionDialogKey from '@/constants/MultiActionDialogKey';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import { useEntityDetails } from '@/modules/EntityDetails';
import { useGoogleAnalytics } from '@/modules/GoogleAnalytics';
import userActionTypes from '@/rdx/actionTypes';
import { useTasksContext } from '../components/TasksProvider';
import useTaskAnalytics, { AnalyticsTarget } from './useTaskAnalytics';
import useTaskServices from './useTaskServices';

export default function useTaskListView() {
  const { openConstituentTaskEntity, openTaskEntity } = useEntityDetails();
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const { constituentId } = useParams();
  const { updateQuery } = useTasksContext();
  const { updateTask } = useTaskServices();
  const { linkClickedEvent } = useGoogleAnalytics();
  const { sendTasksListCreatedAction } = useTaskAnalytics();

  const onCheckboxChange = (e, task) => {
    e.stopPropagation();
    updateTask({ taskId: task.taskId, isCompleted: e.target.checked });
  };

  const onConstituentClick = (e, constituent) => {
    e.stopPropagation();
    history.push(
      generatePath(AuthRoutes.CONSTITUENT, {
        constituentId: constituent.constituentId,
      }),
    );
  };

  const onCreateTaskClick = () => {
    sendTasksListCreatedAction();
    dispatch({
      type: userActionTypes.OPEN_ACTION_DIALOG,
      dialogKey: MultiActionDialogKey.ADD_TASK,
      data: {
        constituentId,
      },
    });
  };

  const onFilterUpdate = (e) => {
    const { name, value } = e.target;
    updateQuery({ [name]: value });
  };

  const onPageChange = (e, value) => {
    updateQuery({ page: value });
  };

  const onPageSizeChange = (e) => {
    const { value } = e.target;
    updateQuery({ pageSize: value });
  };

  const onRowClick = (task) => {
    const analyticsLocation =
      match.path === AuthRoutes.CONSTITUENT_TASKS
        ? AnalyticsLocation.CONSTITUENT
        : AnalyticsLocation.TASKS;

    linkClickedEvent(
      analyticsLocation,
      `${AnalyticsTarget.VIEW_TASK} | ${task.taskTitle}`,
    );
    if (constituentId) {
      openConstituentTaskEntity(constituentId, task.taskId);
    } else {
      openTaskEntity(task.taskId);
    }
  };

  const onSortClick = (property) => () => updateQuery({ sortBy: property });

  return {
    onCheckboxChange,
    onConstituentClick,
    onCreateTaskClick,
    onFilterUpdate,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    onSortClick,
  };
}
